import React from "react";
import {
  Bookmark,
  Globe,
  Heart,
  MessageCircle,
  MoreHorizontal,
  Music2,
  Repeat2,
  Send,
  Share2,
  ThumbsUp,
} from "lucide-react";
import { PlatformType } from "../models/Platform";
import { useFirebaseUrl } from "../hooks/useFirebaseUrl";
import { cn } from "../lib/utils";

export const RENDITION_PLATFORM_LABELS: Record<string, string> = {
  youtube: "YouTube",
  linkedin: "LinkedIn",
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
  pinterest: "Pinterest",
};

interface PlatformPostFrameProps {
  platform: PlatformType;
  /**
   * What this platform receives — decides the chrome: Instagram videos
   * publish as Reels (overlay chrome), while its carousels are feed cards.
   * LinkedIn/Facebook videos stay feed cards (that's how they publish).
   * "story" (IG/FB postType) renders the story surface: progress segments,
   * no feed card, no caption.
   */
  format?: "carousel" | "video" | "story";
  channelName?: string;
  profileImage?: string | null;
  /** Effective caption for this platform (per-connection override or global) */
  caption?: string;
  /** Effective title for platforms that show it in the feed */
  title?: string;
  /** 0-based current slide, for the page pill / dots */
  pageIndex?: number;
  pageCount?: number;
  /** The media — slide canvas or video player, pre-sized by the caller */
  children: React.ReactNode;
}

function Avatar({
  profileImage,
  name,
  className,
}: {
  profileImage?: string | null;
  name?: string;
  className?: string;
}) {
  const { url } = useFirebaseUrl(profileImage ?? undefined);
  return url ? (
    <img src={url} alt="" className={cn("rounded-full object-cover", className)} />
  ) : (
    <div
      className={cn(
        "rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600",
        className
      )}
    >
      {name?.[0]?.toUpperCase() ?? "•"}
    </div>
  );
}

function PagePill({ pageIndex, pageCount }: { pageIndex?: number; pageCount?: number }) {
  if (!pageCount || pageCount <= 1) return null;
  return (
    <div className="absolute top-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white pointer-events-none">
      {(pageIndex ?? 0) + 1}/{pageCount}
    </div>
  );
}

/**
 * Native platform chrome around a post's media, used by the drill-down
 * rendition preview: feed cards for LinkedIn/Facebook/Instagram, dark
 * overlay chrome for TikTok. Cards use width:min-content so the media child
 * (fixed dvh width) establishes the card width and text rows wrap to it —
 * a class-based w-0/min-w-full trick breaks line-clamp's -webkit-box layout.
 * Chrome is decorative only; overlay variants never intercept the media.
 */
export function PlatformPostFrame({
  platform,
  format = "carousel",
  channelName,
  profileImage,
  caption,
  title,
  pageIndex,
  pageCount,
  children,
}: PlatformPostFrameProps) {
  const name = channelName || "Your account";

  // IG/FB stories: 9:16 surface with progress segments; slides show
  // sequentially (no caption on stories)
  if (format === "story") {
    return (
      <div
        className="relative max-w-full rounded-lg overflow-hidden bg-black shadow-lg flex flex-col"
        style={{ width: "min-content", aspectRatio: `${9 / 16}` }}
      >
        <div className="absolute top-2 inset-x-2 z-10 flex gap-1 pointer-events-none">
          {Array.from({ length: Math.min(pageCount ?? 1, 12) }, (_, i) => (
            <span
              key={i}
              className={cn(
                "h-0.5 flex-1 rounded-full",
                i <= Math.min(pageIndex ?? 0, 11) ? "bg-white" : "bg-white/30"
              )}
            />
          ))}
        </div>
        <div className="absolute top-4 left-3 z-10 flex items-center gap-2 pointer-events-none">
          <Avatar profileImage={profileImage} name={name} className="h-7 w-7 ring-1 ring-white/60" />
          <span className="text-white text-xs font-semibold drop-shadow">{name}</span>
          <span className="text-white/70 text-xs drop-shadow">Just now</span>
        </div>
        <div className="flex-1 min-h-0 flex items-center justify-center">
          {children}
        </div>
        <div className="absolute bottom-3 inset-x-3 z-10 flex items-center gap-3 pointer-events-none">
          <div className="flex-1 rounded-full border border-white/50 px-4 py-1.5 text-white/80 text-xs">
            Send message
          </div>
          <Heart className="h-6 w-6 text-white drop-shadow" />
          <Send className="h-6 w-6 text-white drop-shadow" />
        </div>
      </div>
    );
  }

  // Instagram videos publish as Reels — full-bleed overlay chrome
  if (platform === "instagram" && format === "video") {
    return (
      <div className="relative w-fit max-w-full rounded-lg overflow-hidden bg-black shadow-lg">
        {children}
        <div className="absolute right-2 bottom-14 pointer-events-none flex flex-col items-center gap-4 text-white">
          <Heart className="h-6 w-6 drop-shadow" />
          <MessageCircle className="h-6 w-6 drop-shadow" />
          <Send className="h-6 w-6 drop-shadow" />
          <Bookmark className="h-6 w-6 drop-shadow" />
          <MoreHorizontal className="h-5 w-5 drop-shadow" />
        </div>
        <div className="absolute inset-x-0 bottom-0 pointer-events-none bg-gradient-to-t from-black/70 to-transparent px-3 pt-10 pb-3 pr-14">
          <div className="flex items-center gap-2">
            <Avatar profileImage={profileImage} name={name} className="h-7 w-7 ring-1 ring-white/70" />
            <span className="text-white text-sm font-semibold truncate">{name}</span>
          </div>
          {caption && (
            <div
              className="notranslate mt-1 text-white/90 text-xs leading-snug line-clamp-2"
              translate="no"
            >
              {caption}
            </div>
          )}
          <div className="mt-1 flex items-center gap-1.5 text-white/90 text-xs">
            <Music2 className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{name} · Original audio</span>
          </div>
        </div>
      </div>
    );
  }

  if (platform === "tiktok" && format === "video") {
    // Video fills the screen — chrome overlays the footage on a gradient
    return (
      <div className="relative w-fit max-w-full rounded-lg overflow-hidden bg-black shadow-lg">
        {children}
        <div className="absolute right-2 bottom-14 pointer-events-none flex flex-col items-center gap-4 text-white">
          <Avatar profileImage={profileImage} name={name} className="h-8 w-8 ring-2 ring-white" />
          <Heart className="h-6 w-6 drop-shadow" />
          <MessageCircle className="h-6 w-6 drop-shadow" />
          <Bookmark className="h-6 w-6 drop-shadow" />
          <Share2 className="h-6 w-6 drop-shadow" />
        </div>
        <div className="absolute inset-x-0 bottom-0 pointer-events-none bg-gradient-to-t from-black/70 to-transparent px-3 pt-10 pb-3 pr-14">
          <div className="text-white text-sm font-semibold">@{name}</div>
          {caption && (
            <div
              className="notranslate mt-0.5 text-white/90 text-xs leading-snug line-clamp-2"
              translate="no"
            >
              {caption}
            </div>
          )}
          <div className="mt-1 flex items-center gap-1.5 text-white/90 text-xs">
            <Music2 className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">original sound — {name}</span>
          </div>
        </div>
      </div>
    );
  }

  if (platform === "tiktok") {
    // Photo post (mobile look): a 9:16 phone screen laid out as a flex
    // column — For You nav, photo, pagination dots, caption/sound on the
    // black area beneath. Everything flows, so chrome never overlaps the
    // photo; the action rail overlays the photo's lower-right like the app.
    return (
      <div
        className="relative max-w-full rounded-lg overflow-hidden bg-black shadow-lg flex flex-col"
        style={{ width: "min-content", aspectRatio: `${9 / 16}` }}
      >
        <div className="shrink-0 flex items-center justify-center gap-2 text-xs py-2.5">
          <span className="text-white/60 font-medium">Following</span>
          <span className="text-white/30">|</span>
          <span className="text-white font-semibold">For You</span>
        </div>
        <div className="relative">
          {children}
          <div className="absolute right-2 bottom-3 pointer-events-none flex flex-col items-center gap-4 text-white">
            <Avatar profileImage={profileImage} name={name} className="h-8 w-8 ring-2 ring-white" />
            <Heart className="h-6 w-6 drop-shadow" />
            <MessageCircle className="h-6 w-6 drop-shadow" />
            <Bookmark className="h-6 w-6 drop-shadow" />
            <Share2 className="h-6 w-6 drop-shadow" />
          </div>
        </div>
        {pageCount != null && pageCount > 1 && (
          <div className="shrink-0 flex justify-center gap-1 pt-2">
            {Array.from({ length: Math.min(pageCount, 10) }, (_, i) => (
              <span
                key={i}
                className={cn(
                  "h-1 w-1 rounded-full",
                  i === Math.min(pageIndex ?? 0, 9) ? "bg-white" : "bg-white/40"
                )}
              />
            ))}
          </div>
        )}
        <div className="mt-auto min-h-0 overflow-hidden px-3 pb-3 pt-2 pr-14">
          <div className="text-white text-sm font-semibold">@{name}</div>
          {caption && (
            <>
              <div
                className="notranslate mt-0.5 text-white/90 text-xs leading-snug line-clamp-2"
                translate="no"
              >
                {caption}
              </div>
              {caption.length > 100 && (
                <span className="text-xs font-semibold text-white">
                  …See more
                </span>
              )}
            </>
          )}
          <div className="mt-1 flex items-center gap-1.5 text-white/90 text-xs">
            <Music2 className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">original sound — {name}</span>
          </div>
        </div>
      </div>
    );
  }

  if (platform === "pinterest") {
    const pinTitle = title?.trim() || "Pinterest Pin";
    return (
      <div
        className="max-w-full overflow-hidden rounded-2xl bg-white"
        style={{ width: "min-content" }}
      >
        <div className="relative overflow-hidden rounded-2xl bg-gray-100">
          {children}
          <div className="pointer-events-none absolute right-2 top-2 rounded-full bg-[#e60023] px-3 py-1.5 text-xs font-semibold text-white shadow-sm">
            Save
          </div>
        </div>
        {pageCount != null && pageCount > 1 && (
          <div className="flex items-center justify-center gap-1.5 pt-2">
            {Array.from({ length: Math.min(pageCount, 10) }, (_, i) => (
              <span
                key={i}
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  i === Math.min(pageIndex ?? 0, 9)
                    ? "bg-gray-900"
                    : "bg-gray-300"
                )}
              />
            ))}
          </div>
        )}
        <div className="px-2 pb-2 pt-2" style={{ width: "100%" }}>
          <h3
            className={cn(
              title?.trim() && "notranslate",
              "break-words text-sm font-semibold leading-snug text-gray-950 line-clamp-2"
            )}
            translate={title?.trim() ? "no" : undefined}
          >
            {pinTitle}
          </h3>
        </div>
      </div>
    );
  }

  if (platform === "instagram") {
    return (
      <div
        className="max-w-full bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
        style={{ width: "min-content" }}
      >
        <div className="flex items-center gap-2 px-3 py-2">
          <Avatar profileImage={profileImage} name={name} className="h-8 w-8" />
          <span className="text-sm font-semibold text-gray-900 truncate">{name}</span>
          <MoreHorizontal className="h-4 w-4 text-gray-700 ml-auto shrink-0" />
        </div>
        <div className="relative">
          {children}
          <PagePill pageIndex={pageIndex} pageCount={pageCount} />
        </div>
        <div className="flex items-center gap-4 px-3 pt-2.5 text-gray-900">
          <Heart className="h-6 w-6" />
          <MessageCircle className="h-6 w-6" />
          <Send className="h-6 w-6" />
          <Bookmark className="h-6 w-6 ml-auto" />
        </div>
        {pageCount != null && pageCount > 1 && (
          <div className="flex items-center justify-center gap-1 pt-2">
            {Array.from({ length: Math.min(pageCount, 10) }, (_, i) => (
              <span
                key={i}
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  i === Math.min(pageIndex ?? 0, 9) ? "bg-sky-500" : "bg-gray-300"
                )}
              />
            ))}
          </div>
        )}
        {caption ? (
          <div className="px-3 pt-2 pb-3">
            <p
              className="notranslate text-sm text-gray-900 leading-snug line-clamp-2"
              translate="no"
            >
              <span className="font-semibold">{name}</span> {caption}
            </p>
            {caption.length > 100 && (
              <span className="text-sm text-gray-500">… more</span>
            )}
          </div>
        ) : (
          <div className="pb-3" />
        )}
      </div>
    );
  }

  // LinkedIn / Facebook feed card
  const isLinkedin = platform === "linkedin";
  const actions: Array<[string, React.ComponentType<{ className?: string }>]> =
    isLinkedin
      ? [
          ["Like", ThumbsUp],
          ["Comment", MessageCircle],
          ["Repost", Repeat2],
          ["Send", Send],
        ]
      : [
          ["Like", ThumbsUp],
          ["Comment", MessageCircle],
          ["Share", Share2],
        ];

  return (
    <div
      className={cn(
        "max-w-full bg-white border border-gray-200 shadow-sm overflow-hidden",
        isLinkedin ? "rounded-lg" : "rounded-xl"
      )}
      style={{ width: "min-content" }}
    >
      <div className="flex items-center gap-2 px-3 pt-3 pb-2">
        <Avatar
          profileImage={profileImage}
          name={name}
          className={isLinkedin ? "h-10 w-10" : "h-9 w-9"}
        />
        <div className="min-w-0 leading-tight">
          <div className="text-sm font-semibold text-gray-900 truncate">{name}</div>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <span>{isLinkedin ? "Now" : "Just now"}</span>
            <span>·</span>
            <Globe className="h-3 w-3" />
          </div>
        </div>
      </div>
      {caption && (
        <div className="px-3 pb-2">
          <p
            className="notranslate text-sm text-gray-900 leading-snug line-clamp-2"
            translate="no"
          >
            {caption}
          </p>
          {caption.length > 120 && (
            <span className="text-sm text-gray-500">…see more</span>
          )}
        </div>
      )}
      <div className="relative">
        {children}
        <PagePill pageIndex={pageIndex} pageCount={pageCount} />
      </div>
      {/* w-0/min-w-full keeps the labeled actions' intrinsic width from
          stretching the card past the media (safe on plain flex rows —
          unlike on line-clamp text) */}
      <div className="w-0 min-w-full flex items-center justify-around overflow-hidden border-t border-gray-100 px-1 py-1">
        {actions.map(([label, Icon]) => (
          <span
            key={label}
            className="flex items-center gap-1 px-1 py-1.5 text-[11px] font-medium text-gray-500"
          >
            <Icon className="h-4 w-4 shrink-0" />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
